/**
 * COPY TO COLLECTION MODAL
 * 
 * Modal for copying a brand item to user's personal collection
 * Pre-fills information from the brand item and lets user customize
 * 
 * UPDATED: Now tracks photo source for DMCA compliance
 */

import { useState } from 'react';
import { AppIcons } from '../../constants/icons';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { getCurrentUser } from '../../firebase/auth';
import type { BrandItem, AggregatedItem } from '../../types/brand';
import { itemDocKeys } from '../../utils/itemDocKeys';
import { normalizeTypography } from '../../utils/normalizeTypography';

interface CopyToCollectionModalProps {
  item: BrandItem | AggregatedItem;
  brandName: string;
  brandId?: string;
  asWishlist?: boolean;  // If true, add to wishlist instead of collection
  onClose: () => void;
  onSuccess: () => void;
}

export default function CopyToCollectionModal({
  item,
  brandName,
  brandId,
  asWishlist = false,
  onClose,
  onSuccess
}: CopyToCollectionModalProps) {
  const [quantity, setQuantity] = useState(1);
  const [storage, setStorage] = useState('');
  const [price, setPrice] = useState('');
  const [notes, setNotes] = useState('');
  const [isoPriority, setIsoPriority] = useState<'iso' | 'diso' | 'unicorn'>('iso');
  
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const currentUser = getCurrentUser();
  const isBrandItem = 'status' in item;

  // Get photo for display - handle multiple formats:
  // 1. BrandItem: photos is [{url: string}]
  // 2. AggregatedItem: has representativePhoto?.url
  // 3. Brand Product from BrandPage: photos is string[]
  const getPhotoUrl = (): string | undefined => {
    // Check for photos array
    if ('photos' in item && item.photos && Array.isArray(item.photos) && item.photos.length > 0) {
      const firstPhoto = item.photos[0];
      // If it's already a string URL
      if (typeof firstPhoto === 'string') {
        return firstPhoto;
      }
      // If it's an object with url property
      if (typeof firstPhoto === 'object' && firstPhoto && 'url' in firstPhoto) {
        return (firstPhoto as {url: string}).url;
      }
    }
    // Check for representativePhoto (AggregatedItem)
    if ('representativePhoto' in item && item.representativePhoto?.url) {
      return item.representativePhoto.url;
    }
    return undefined;
  };

  // Get credit info if the source photo has it
  const getPhotoCredit = (): { creditName?: string; creditUrl?: string } => {
    if ('photos' in item && item.photos && Array.isArray(item.photos) && item.photos.length > 0) {
      const firstPhoto = item.photos[0];
      if (typeof firstPhoto === 'object' && firstPhoto) {
        return {
          creditName: (firstPhoto as any).creditName,
          creditUrl: (firstPhoto as any).creditUrl,
        };
      }
    }
    if ('representativePhoto' in item && item.representativePhoto) {
      return {
        creditName: (item.representativePhoto as any).creditName,
        creditUrl: (item.representativePhoto as any).creditUrl,
      };
    }
    return {};
  };
  
  const photoUrl = getPhotoUrl();
  const photoCredit = getPhotoCredit();

  // Handle save
  const handleSave = async () => {
    if (!currentUser) {
      setError('You must be logged in to add items');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      // Prefer the item's own itemType when it carries one (Phase 1 added
      // this to AggregatedItem and BrandProduct; before that we assumed
      // everything was a polish). Fall back to 'polish' so pre-launch
      // brand catalog records and callers that build items ad hoc
      // continue to work.
      const resolvedItemType = (item as { itemType?: string }).itemType ?? 'polish';

      // Normalize brand + name once so the copied item matches the
      // manual-add flow's normalized shape. Without this, brand strings
      // written verbatim from a brand page (with trailing whitespace,
      // smart quotes, etc.) create case- or typography-only duplicates
      // that split Top Brands and the collection view.
      const normalizedBrand = normalizeTypography(String(brandName ?? '').trim());
      const normalizedName = normalizeTypography(String(item.name ?? '').trim());
      // Build the item document
      const newItem: any = {
        // Core fields
        name: normalizedName,
        brand: normalizedBrand,
        itemType: resolvedItemType,
        // Derived slug/lower fields so this item is discoverable by the
        // community aggregation query (indexed on itemType+brandSlug+
        // nameSlug). Without these, adding via Copy-to-Collection produces
        // items invisible to their own detail page — the exact bug the
        // Fix A / backfill run addressed for direct-write AddModals.
        ...itemDocKeys(normalizedBrand, normalizedName),
        category: item.category || 'Nail Lacquer',

        // From brand item
        colorDescription: item.colorDescription || '',
        collectionName: item.collectionName || '',

        // User inputs
        quantity: asWishlist ? 1 : quantity,
        ...(storage && { storage }),
        ...(price && { price: parseFloat(price) }),
        ...(notes && { notes }),

        // Wishlist vs Collection
        onWishlist: asWishlist,
        ...(asWishlist && { isoPriority }),

        // Default toggle states
        isFavorite: false,
        isRareLE: false,
        needsInfo: false,
        swatched: false,
        timesUsed: 0,
        isForDestash: false,

        // Timestamps
        dateAdded: serverTimestamp(),
        updatedAt: serverTimestamp(),

        // Track origin
        ...(brandId && { copiedFromBrand: brandId }),
        ...('id' in item && { copiedFromBrandItem: item.id }),
      };

      // Copy over tags if they exist on brand item
      if (isBrandItem) {
        const brandItem = item as BrandItem;
        if (brandItem.finish) newItem.finish = brandItem.finish;
        if (brandItem.holoType) newItem.holoType = brandItem.holoType;
        if (brandItem.colorShift) newItem.colorShift = brandItem.colorShift;
        if (brandItem.particles) newItem.particles = brandItem.particles;
        if (brandItem.specialFeatures) newItem.specialFeatures = brandItem.specialFeatures;
        if (brandItem.colorFamily) newItem.colorFamily = brandItem.colorFamily;
      }

      // Copy photo if available WITH SOURCE TRACKING for DMCA. No caption
      // is set — a source label ("From brand catalog" / "From community")
      // used to be stamped here but was redundant with the tile-level
      // Community/verified badge. DMCA source tracking on the `source`
      // field is what actually matters for takedown compliance.
      if (photoUrl) {
        newItem.photos = [{
          url: photoUrl,
          isDefault: true,
          // DMCA Source Tracking
          uploadedBy: currentUser.uid,
          uploadedAt: new Date().toISOString(),
          source: isBrandItem ? 'brand' : 'community',
          sourceUrl: photoUrl,                // Original URL
          dmcaStatus: 'active',
          // Preserve credit if it exists on the source
          ...(photoCredit.creditName && { creditName: photoCredit.creditName }),
          ...(photoCredit.creditUrl && { creditUrl: photoCredit.creditUrl }),
        }];
      }

      // Save to user's items collection
      await addDoc(
        collection(db, 'users', currentUser.uid, 'items'),
        newItem
      );

      setSuccess(true);
      
      // Brief delay then close
      setTimeout(() => {
        onSuccess();
      }, 1500);

    } catch (err) {
      console.error('Error adding item:', err);
      setError('Failed to add item to your collection');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="glass-card max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className={`p-4 ${asWishlist ? 'bg-gradient-to-r from-amber-600 to-yellow-600' : 'bg-slate-700'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-white">
              {asWishlist ? (
                <AppIcons.Wishlist className="w-5 h-5" />
              ) : (
                <AppIcons.Add className="w-5 h-5" />
              )}
              <h2 className="text-lg font-bold">
                Add to {asWishlist ? 'Wishlist' : 'Collection'}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="p-1 text-white/80 hover:text-white hover:bg-white/20 rounded-full transition-colors"
            >
              <AppIcons.Close className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4">
          {/* Success State */}
          {success && (
            <div className="flex flex-col items-center justify-center py-8">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 ${asWishlist ? 'bg-amber-600/30' : 'bg-purple-600/30'}`}>
                <AppIcons.Confirm className={`w-8 h-8 ${asWishlist ? 'text-amber-400' : 'text-purple-400'}`} />
              </div>
              <h3 className="text-xl font-bold text-glass mb-2">Added!</h3>
              <p className="text-glass-secondary">
                {item.name} has been added to your {asWishlist ? 'wishlist' : 'collection'}.
              </p>
            </div>
          )}

          {/* Form */}
          {!success && (
            <>
              {/* Error */}
              {error && (
                <div className="flex items-center gap-2 p-3 mb-4 bg-red-600/30 border border-red-500/50 rounded-lg text-red-300">
                  <AppIcons.Error className="w-5 h-5 flex-shrink-0" />
                  <span className="text-sm">{error}</span>
                </div>
              )}

              {/* Item Preview */}
              <div className="flex gap-4 p-4 bg-slate-600/50 rounded-lg mb-4">
                {photoUrl ? (
                  <img
                    src={photoUrl}
                    alt={item.name}
                    className="w-16 h-16 rounded-lg object-cover"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-lg bg-slate-700 flex items-center justify-center">
                    <AppIcons.ViewAll className="w-6 h-6 text-glass-muted" />
                  </div>
                )}
                <div className="flex-grow min-w-0">
                  <h3 className="font-medium text-glass truncate">{item.name}</h3>
                  <p className="text-sm text-glass-secondary">{brandName}</p>
                  {item.collectionName && (
                    <p className="text-xs text-purple-400 mt-1">{item.collectionName}</p>
                  )}
                </div>
              </div>

              {/* Form Fields */}
              <div className="space-y-4">
                {/* Quantity (only for collection, not wishlist) */}
                {!asWishlist && (
                  <div>
                    <label className="block text-sm font-medium text-glass-secondary mb-1">
                      Quantity
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={quantity}
                      onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
                      className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-glass focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                )}

                {/* ISO Priority (only for wishlist) */}
                {asWishlist && (
                  <div>
                    <label className="block text-sm font-medium text-glass-secondary mb-2">
                      How badly do you want this?
                    </label>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setIsoPriority('iso')}
                        className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                          isoPriority === 'iso'
                            ? 'bg-green-600/30 text-green-300 border-2 border-green-500/50'
                            : 'border-2 border-slate-600 text-glass-secondary hover:bg-slate-600/50'
                        }`}
                      >
                        ISO
                      </button>
                      <button
                        type="button"
                        onClick={() => setIsoPriority('diso')}
                        className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                          isoPriority === 'diso'
                            ? 'bg-orange-600/30 text-orange-300 border-2 border-orange-500/50'
                            : 'border-2 border-slate-600 text-glass-secondary hover:bg-slate-600/50'
                        }`}
                      >
                        DISO
                      </button>
                      <button
                        type="button"
                        onClick={() => setIsoPriority('unicorn')}
                        className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                          isoPriority === 'unicorn'
                            ? 'bg-purple-600/30 text-purple-300 border-2 border-purple-500/50'
                            : 'border-2 border-slate-600 text-glass-secondary hover:bg-slate-600/50'
                        }`}
                      >
                        🦄 Unicorn
                      </button>
                    </div>
                  </div>
                )}

                {/* Storage Location (only for collection) */}
                {!asWishlist && (
                  <div>
                    <label className="block text-sm font-medium text-glass-secondary mb-1">
                      Storage Location (optional)
                    </label>
                    <input
                      type="text"
                      value={storage}
                      onChange={(e) => setStorage(e.target.value)}
                      placeholder="e.g., Helmer 2, Drawer 3"
                      className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-glass placeholder-slate-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                )}

                {/* Purchase Price (only for collection) */}
                {!asWishlist && (
                  <div>
                    <label className="block text-sm font-medium text-glass-secondary mb-1">
                      Purchase Price (optional)
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-glass-muted">$</span>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={price}
                        onChange={(e) => setPrice(e.target.value)}
                        placeholder="0.00"
                        className="w-full pl-7 pr-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-glass placeholder-slate-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                )}

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-glass-secondary mb-1">
                    Notes (optional)
                  </label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder={asWishlist ? "Where to find it, why you want it..." : "Any notes about this polish..."}
                    rows={2}
                    className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-glass placeholder-slate-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                  />
                </div>
              </div>

              {/* Help text */}
              <p className="text-xs text-glass-muted mt-4">
                💡 You can edit all details after adding to your {asWishlist ? 'wishlist' : 'collection'}.
              </p>
            </>
          )}
        </div>

        {/* Footer */}
        {!success && (
          <div className="flex items-center justify-end gap-3 p-4 border-t border-slate-600 bg-slate-700/50">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-glass-secondary hover:bg-slate-600/50 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className={`px-6 py-2 text-white rounded-lg font-medium transition-colors ${
                saving
                  ? 'bg-gray-400 cursor-not-allowed'
                  : asWishlist
                    ? 'bg-amber-500 hover:bg-amber-600'
                    : 'bg-purple-600 hover:bg-purple-700'
              }`}
            >
              {saving ? 'Adding...' : `Add to ${asWishlist ? 'Wishlist' : 'Collection'}`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
